import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import CarCard from "../components/CarCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BODY_TYPES = ["Sedan", "SUV", "Coupe", "Convertible"];
const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const SORTS = [
  { v: "newest", label: "Newest first" },
  { v: "price_asc", label: "Price · Low to High" },
  { v: "price_desc", label: "Price · High to Low" },
  { v: "year_desc", label: "Year · Newest" },
];

export default function Inventory() {
  const [cars, setCars] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const [brand, setBrand] = useState("all");
  const [bodyType, setBodyType] = useState("all");
  const [fuel, setFuel] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/cars/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (brand !== "all") params.brand = brand;
    if (bodyType !== "all") params.body_type = bodyType;
    if (fuel !== "all") params.fuel = fuel;
    if (minPrice) params.min_price = Number(minPrice) * 100000;
    if (maxPrice) params.max_price = Number(maxPrice) * 100000;
    api.get("/cars", { params })
      .then((r) => setCars(r.data))
      .finally(() => setLoading(false));
  }, [brand, bodyType, fuel, minPrice, maxPrice]);

  const sorted = useMemo(() => {
    const arr = [...cars];
    switch (sort) {
      case "price_asc": arr.sort((a, b) => a.price - b.price); break;
      case "price_desc": arr.sort((a, b) => b.price - a.price); break;
      case "year_desc": arr.sort((a, b) => b.year - a.year); break;
      default: break;
    }
    return arr;
  }, [cars, sort]);

  const reset = () => {
    setBrand("all"); setBodyType("all"); setFuel("all");
    setMinPrice(""); setMaxPrice(""); setSort("newest");
  };

  return (
    <div data-testid="inventory-page" className="bg-[#FCFCFC] min-h-screen">
      <div className="border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <p className="overline mb-4">The Inventory</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-light">
            Hand-picked luxury automobiles.
          </h1>
          <p className="text-gray-600 mt-6 max-w-2xl">
            Every car in our catalogue has been inspected, authenticated and assured by the Royale Yaan team.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-10">
        {/* Filters */}
        <div className="bg-white border border-gray-200 p-5 md:p-6 mb-10" data-testid="filters-bar">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger data-testid="filter-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={bodyType} onValueChange={setBodyType}>
              <SelectTrigger data-testid="filter-body"><SelectValue placeholder="Body" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Body Types</SelectItem>
                {BODY_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fuel} onValueChange={setFuel}>
              <SelectTrigger data-testid="filter-fuel"><SelectValue placeholder="Fuel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fuel Types</SelectItem>
                {FUELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              data-testid="filter-min-price"
              placeholder="Min ₹L"
              type="number"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
            />
            <Input
              data-testid="filter-max-price"
              placeholder="Max ₹L"
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
            />
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="filter-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-between items-center mt-5">
            <p className="text-xs text-gray-500" data-testid="filter-count">
              {loading ? "Loading…" : `${sorted.length} listing${sorted.length === 1 ? "" : "s"}`}
            </p>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="filter-reset" className="text-xs">
              Reset filters
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[420px] bg-white border border-gray-200 animate-pulse" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-gray-300 bg-white" data-testid="inventory-empty">
            <p className="font-display text-2xl mb-2">No cars match your filters.</p>
            <p className="text-sm text-gray-500">Try resetting the filters above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="inventory-grid">
            {sorted.map((c, i) => <CarCard key={c.id} car={c} idx={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
